import db from "../db/database";
import { origenDelFallo } from "../utils/avisoDelFallo";
import {
  fetchFromBusinessCentral,
  fetchPagedFromBusinessCentral,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { updateClientNoInInvoices } from "./invoiceService";
import { getPendingSync, isOnline } from "./syncService";
import { documentoIdentifica, mismoEspacioDeIdentidad } from "./identidadDelCliente";
import { avisoDeDimensiones } from "./avisoDeDimensiones";
import { marcaConSolape } from "./margenDeSolape";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import {
  anotarAvanceDeLectura,
  anotarError,
  anotarFila,
  anotarInicioDeLote,
  anotarLectura,
  anotarResumenDelLote,
  anotarSinConexion,
} from "./syncLog";
import {
  propiedadDelTipoDeDocumento,
  resolverTipoDeDocumento,
} from "../constants/identityDocuments";
import { COUNTRY_CODES } from "../constants/countries";
import {
  clavesDelClienteDesdeBC,
  clavesDelClienteParaBC,
} from "../components/Invoice/extensions/panama/datosFiscalesDePanama";

// Las claves que sólo tiene el cliente de Panamá. El spread de `...client` las
// arrastra a cualquier país si la ficha las tuviera, así que fuera de Panamá se
// retiran, como el tipo de documento.
const CLAVES_DE_PANAMA = ["Tipo_Cliente_FE", "Ruc_DV", "Codigo_Ubicacion_FE", "Provincia"];
// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "clientes";

// BC puede devolver alias de tipo de documento (ej. "ID" en vez de "RUC" para Ecuador).
// Normalizamos siempre al valor canónico antes de persistir en la BD local.
const getActiveCountryCode = async () => {
  const env = await db.environment.filter((e) => e.isActive === true).first();
  return env?.country;
};

/**
 * Un cliente en el formato que espera Business Central.
 *
 * `countryCode` decide bajo qué nombre viaja el tipo de documento: Costa Rica lo
 * espera en `Tax_Identification_Type` y el resto en `Tipo_Documento_SrI`. Sin
 * país se usa el nombre de siempre, que es lo que hacía antes de esto.
 */
export const transformClientForBC = (client, countryCode) => {
  //Incluimos todas las propiedades del cliente ya que en algunos paises son más
  const transformedClient = { ...client };

  // Asignamos o sobreescribimos los campos obligatorios o con default
  transformedClient.No_ = client.No || "TEMP"; // BC asigna si no existe
  transformedClient.Name = client.name || "";
  transformedClient.Direccion = client.Direccion || "";
  transformedClient.Direccion_2 = client.Direccion_2 || "";
  transformedClient.Poblacion = client.Poblacion || "";
  transformedClient.Cod_Postal = client.Cod_Postal || "";
  transformedClient.Pais = client.Pais || "";
  transformedClient.Telefono = client.Telefono || "";
  transformedClient.Mobil = client.Mobil || "";
  transformedClient.E_Mail = client.E_Mail || "";
  transformedClient.Customer_Price_Group = client.Customer_Price_Group || "";
  transformedClient.SUNAT_Document = client.SUNAT_Document || "";
  transformedClient.Customer_Type = client.Customer_Type || "";
  transformedClient.Vendor_Type = client.Vendor_Type || "";
  transformedClient.Customer_Disc_Group = client.Customer_Disc_Group || "";
  transformedClient.Contacto = client.Contacto || "";
  // Se manda bajo el nombre que espera el pais, y solo donde el pais lo tiene:
  // fuera de su sitio, la clave sobrante haria que Business Central escribiera
  // un campo que a ese pais no le corresponde.
  // La clave la decide el país, y la que no toca se retira: mandarle a Ecuador
  // un `Tax_Identification_Type` --o a Costa Rica un `Tipo_Documento_SrI`-- es
  // pedirle a Business Central que escriba un campo que a ese país no le
  // corresponde. El spread de `...client` mete siempre el nombre local, así que
  // hay que quitarlo cuando no es el bueno.
  const claveDelTipo = propiedadDelTipoDeDocumento(countryCode);
  if (claveDelTipo !== "Tipo_Documento_SrI") delete transformedClient.Tipo_Documento_SrI;
  transformedClient[claveDelTipo] = client.Tipo_Documento_SrI || null;
  transformedClient.Numero_Identificacion_fiscal = client.Numero_Identificacion_fiscal || "";
  transformedClient.POS_Discount = client.POS_Discount ?? 0;
  // El territorio sólo viaja si el cliente lo trae.
  //
  // Era `client.Territory_Code || 217`, y el terminal **no captura el territorio
  // en ninguna pantalla**: no hay un solo sitio más en la aplicación que lo
  // escriba. O sea que todas las altas, de los seis países, mandaban 217 --un
  // número puesto en el commit inicial, sin razón escrita--, y ese 217 pisaba el
  // `Territory Code` de la plantilla de cliente en cada alta.
  //
  // Con la plantilla por tienda eso pasó de rareza a defecto: la plantilla es el
  // valor por defecto de lo que el terminal no manda, y mandar un territorio que
  // nadie tecleó es justamente impedir que lo ponga.
  if (client.Territory_Code) transformedClient.Territory_Code = client.Territory_Code;
  else delete transformedClient.Territory_Code;
  transformedClient.Deleted = client.deleted || false;

  // El bloqueo **no se manda**: lo decide Business Central y el terminal sólo lo
  // lee para no dejar facturar. Viajaba de rebote en el spread de `...client`
  // desde que el campo se guarda en la ficha local.
  //
  // Lo que pasa allá si se manda, medido en el fuente el 21/09/2026: el
  // importador copia al cliente real sólo los campos que difieren de un registro
  // en blanco (`if CustTFldRef.Value <> VacioFldRef.Value`). Una cadena vacía no
  // casa con su `case` y el campo se queda en la opción 0, que es justo el
  // blanco, así que **no se copia**: la ficha sin bloqueo es inerte. Pero un
  // valor bueno --`All`, `Invoice`, `Ship`-- sí difiere del blanco, sí se copia,
  // y entonces **el terminal le estaría escribiendo el bloqueo al maestro de
  // clientes**, reenviándole a Business Central lo que él mismo nos dijo.
  //
  // Con la 1.0.4.132, que quitó el filtro de la página, esos valores buenos ya
  // nos llegan. Antes no: todo venía en blanco y reenviarlo no hacía nada.
  //
  // Nota para quien venga a buscar lo contrario: por esa acción **tampoco se
  // puede desbloquear** a nadie, porque mandar un espacio coincide con el blanco
  // y tampoco se copia. No da error; simplemente no ocurre.
  delete transformedClient.Blocked;

  // Panamá: lo que la factura electrónica lee de la ficha del cliente --tipo de
  // cliente, DV y ubicación de la DGI--, y con la ubicación, los nombres de
  // provincia, distrito y corregimiento que Business Central pone en el XML. Ver
  // `panama/datosFiscalesDePanama.js`.
  if (countryCode === COUNTRY_CODES.PANAMA) {
    Object.assign(transformedClient, clavesDelClienteParaBC(client));
  } else {
    CLAVES_DE_PANAMA.forEach((clave) => delete transformedClient[clave]);
  }

  // Creamos el formato final esperado por el end point
  return {
    data: JSON.stringify({ Customers: [transformedClient] }),
  };
};

// Payload con todos los clientes que se manden en la misma llamada.
// Desde la extensión 1.0.4.33, Importaciones_insertaClientes procesa cada
// cliente aislado en una try function y devuelve un resultado por registro, así
// que un rechazo ya no arrastra a los demás ni revierte el lote entero.
const transformClientsForBC = (clients, countryCode) => ({
  data: JSON.stringify({
    Customers: clients.map(
      (client) => JSON.parse(transformClientForBC(client, countryCode).data).Customers[0]
    ),
  }),
});

// Nombre con el que se identifica al cliente en el cuadro de resultados.
const nombreDeCliente = (client) => client.name || client.No || "(sin nombre)";

// Campos que se toman de BC al traer un cliente. Antes esta lista estaba
// duplicada en la rama de actualización y en la de alta. Si BC no devuelve el
// campo Tipo_Documento_SrI, conservamos el valor que ya tenía el cliente local
// (si lo hay) en vez de inventar uno.
const mapServerClientToLocal = (serverClient, countryCode, localClient) => ({
  ...serverClient,
  No: serverClient.No,
  name: serverClient.Name,
  Direccion: serverClient.Direccion || "",
  Direccion_2: serverClient.Direccion_2 || "",
  Poblacion: serverClient.Poblacion || "",
  Cod_Postal: serverClient.Cod_Postal || "",
  Pais: serverClient.Pais || "",
  Telefono: serverClient.Telefono || "",
  Mobil: serverClient.Mobil || "",
  E_Mail: serverClient.E_Mail || "",
  Customer_Price_Group: serverClient.Customer_Price_Group || "",
  Contacto: serverClient.Contacto || "",
  // El nombre de la propiedad depende del pais: Costa Rica lo publica como
  // Tax_Identification_Type y el resto como Tipo_Documento_SrI. El campo local
  // se sigue llamando igual en todos.
  Tipo_Documento_SrI:
    resolverTipoDeDocumento(serverClient, countryCode) || localClient?.Tipo_Documento_SrI || null,
  Numero_Identificacion_fiscal: serverClient.Numero_Identificacion_fiscal || "",
  POS_Discount: serverClient.POS_Discount || 0,
  Source_Counter: serverClient.Source_Counter || 0,
  // El bloqueo, explícito y no de rebote.
  //
  // Llegaba ya por el spread de `...serverClient`, pero de prestado: cualquiera
  // que acotara los campos que se copian lo tiraría sin enterarse, y la regla
  // de «a un cliente bloqueado no se le factura» dejaría de funcionar sin que
  // nada avisara. Ver `clienteBloqueado.js`.
  //
  // Se guarda tal cual lo manda Business Central, incluido su espacio de «sin
  // valor»: normalizar es cosa de quien lo lee, y aquí no se decide nada.
  Blocked: serverClient.Blocked ?? "",
  // Panamá: si Business Central no publica la clave, se conserva lo que ya tenía
  // el terminal; si la publica vacía, se escribe vacía. Así «no se sabe» no se
  // confunde con «falta» al validar la venta.
  ...(countryCode === COUNTRY_CODES.PANAMA ? clavesDelClienteDesdeBC(serverClient, localClient) : {}),
  lastModified: new Date(),
  lastSynced: new Date(),
});

// BC rechazó la baja: se revierte el borrado local para que el cliente vuelva
// a aparecer en el listado y local y BC no queden divergentes. Mismo criterio
// que en userSyncService, turnoSyncService y tpvSyncService.
const restoreDeletedClient = async (client, reason) => {
  await db.clients.update(client.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Sincroniza un solo cliente contra BC. La usan el botón de sincronizar de cada
// fila, el alta de cliente desde la factura (InvoiceForm) y el lote syncClients.
//
// Devuelve { ok, message, origen, client }:
//
//   · `client` queda porque InvoiceForm necesita el registro ya sincronizado
//     para seguir armando la factura.
//   · `origen` sólo viene cuando `ok` es falso, y dice de dónde salió el fallo:
//     `"bc"` --un rechazo con motivo que el cajero puede corregir--, `"offline"`
//     --sin conexión, detectado antes de salir-- o `"red"` --la petición no
//     llegó a tener respuesta: timeout, DNS, servidor caído--. Lo etiqueta este
//     servicio, que es quien lo sabe; cada pantalla decide qué enseña.
export const syncClientById = async (clientId) => {
  if (!isOnline()) {
    // `origen` dice de dónde viene el fallo, porque no todos los motivos son
    // para el cajero: el de Business Central sí --«ya existe ese documento»,
    // «falta el grupo contable»-- y el de red no --«timeout of 30000ms
    // exceeded»--. Lo etiqueta el servicio, que es quien lo sabe; la pantalla
    // sólo decide si lo enseña.
    return { ok: false, message: "No hay conexión a internet", origen: "offline", client: null };
  }

  try {
    const client = await db.clients.get(clientId);
    if (!client) {
      return { ok: false, message: "Cliente no encontrado", client: null };
    }

    if (client.deleted) {
      // Un cliente sin No nunca llegó a BC: sólo existe en local.
      if (!client.No) {
        await db.clients.delete(client.id);
        return { ok: true, message: "Cliente eliminado", client: null };
      }

      // Hay que pedirle la baja a BC antes de borrarlo en local. BC la puede
      // rechazar por integridad referencial (ej. un cliente con facturas
      // registradas), y en ese caso el cliente tiene que volver al listado.
      const deleteResponse = await sendToBusinessCentral(
        "Importaciones_insertaClientes",
        transformClientForBC(client, await getActiveCountryCode()),
        true
      );
      const deleteResult = parseImportResponse(deleteResponse, client.No);

      if (!deleteResult.ok) {
        await restoreDeletedClient(client, deleteResult.message);
        return {
          ok: false,
          message:
            deleteResult.message ||
            "Business Central no aplicó la baja. El cliente sigue en el listado.",
          client: await db.clients.get(client.id),
        };
      }

      await db.clients.delete(client.id);
      return { ok: true, message: "Cliente dado de baja en Business Central", client: null };
    }

    const response = await sendToBusinessCentral(
      "Importaciones_insertaClientes",
      transformClientForBC(client, await getActiveCountryCode()),
      true
    );
    const saveResult = parseImportResponse(response, client.No || "TEMP");

    if (!saveResult.ok || !saveResult.assignedNo) {
      return {
        ok: false,
        message:
          saveResult.message || `Respuesta inválida de BC al sincronizar el cliente ${client.id}`,
        origen: "bc",
        client,
      };
    }

    const clientNo = saveResult.assignedNo;
    await db.clients.update(client.id, { No: clientNo, lastModified: new Date() });

    const updatedClient = await fetchFromBusinessCentral(
      `ListaClientes?$filter=No eq '${clientNo}'`
    );

    // Se lee antes de los updates de abajo, que son los que borran el motivo.
    const teniaBajaRechazada = Boolean(client.syncError);

    // El aviso de dimensiones que BC pega al `Mensaje` se guarda en la fila, para
    // avisar al elegir al cliente en una venta: sin él, la primera venta sale
    // rechazada al registrar. El mensaje entero sigue yendo al cuadro. Si esta
    // respuesta ya no lo trae, se limpia. Acuerdo
    // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, fila H5 · TPV.
    const avisoDeBC = avisoDeDimensiones(saveResult.message);

    if (updatedClient.length > 0) {
      const bc = updatedClient[0];
      const countryCode = await getActiveCountryCode();
      await db.clients.update(client.id, {
        ...bc,
        name: bc.Name || client.name,
        Tipo_Documento_SrI:
          resolverTipoDeDocumento(bc, countryCode) || client.Tipo_Documento_SrI,
        lastModified: new Date(),
        lastSynced: new Date(),
        pendingSync: 0,
        syncError: null,
        avisoDeBC,
      });
    } else {
      await db.clients.update(client.id, {
        pendingSync: 0,
        lastSynced: new Date(),
        syncError: null,
        avisoDeBC,
      });
    }

    await updateClientNoInInvoices(client.id, clientNo);

    return {
      ok: true,
      // El texto lo pone Business Central cuando lo manda.
      //
      // `MensajeAccion` arma «Cliente C-1042 creado» en todo registro correcto,
      // y desde el acuerdo de la plantilla por tienda le pega detrás el aviso de
      // la caja que no resolvió. Se deja pasar entero en vez de intentar
      // reconocer el aviso: viaja concatenado y sin clave propia --así se cerró
      // la ronda 5--, así que el terminal no tiene forma de distinguirlo sin
      // atarse a un literal del AL de BC, ni por qué tenerla. De paso, cualquier
      // aviso que BC añada después llega solo.
      //
      // El literal queda de respaldo para el formato viejo de respuesta, donde
      // `parseImportResponse` devuelve `message: null`.
      message: conAvisoDeBajaRechazada(
        saveResult.message || "Cliente sincronizado correctamente.",
        teniaBajaRechazada
      ),
      client: await db.clients.get(client.id),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${clientId}`, error);

    // BC rechaza con HTTP 400 y el motivo real dentro de
    // error.response.data.error.message; error.message sería sólo
    // "Request failed with status code 400".
    const client = await db.clients.get(clientId);
    if (client?.deleted && client?.No) {
      await restoreDeletedClient(client, formatAxiosError(error, null));
    }

    return {
      ok: false,
      message: formatAxiosError(
        error,
        `No se pudo sincronizar el cliente ${clientId} con Business Central`
      ),
      // Con `response` hay respuesta de Business Central --un 400 con el motivo
      // dentro, que sí es para el cajero--. Sin ella el fallo es de red o del
      // propio navegador, y `formatAxiosError` devuelve el `error.message` en
      // crudo: «Network Error», «timeout of 30000ms exceeded». Eso no le dice
      // nada a quien está cobrando. La distinción vive en `avisoDelFallo.js`,
      // que es de donde la toma también el registro de la venta.
      origen: origenDelFallo(error),
      client: (await db.clients.get(clientId)) || null,
    };
  }
};

export const syncClients = async () => {
  if (!isOnline()) {
    anotarSinConexion(ENTIDAD);
    return false;
  }

  // Clientes cuyo push a BC falló (o fue ignorado por BC) en esta corrida.
  // Cada entrada es { client, action: "delete" | "save", reason }. Antes los
  // errores sólo se escribían en consola y la función devolvía true igual, así
  // que un rechazo de BC pasaba por sincronización completada.
  const failedClients = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos.
  const detalle = [];

  try {
    const pendingClients = await getPendingSync("clients");

    if (pendingClients.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingClients);

      for (const client of soloLocales) {
        await db.clients.delete(client.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: nombreDeCliente(client),
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminado sólo en local: nunca llegó a Business Central",
            },
            client.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        anotarInicioDeLote(ENTIDAD, aEnviar.length);

        // UNA llamada para todos los pendientes, en vez de una por cliente.
        //
        // No se delega en syncClientById como antes: esa función manda un
        // cliente por viaje y además vuelve a consultarlo en BC para refrescar
        // sus campos, lo que devolvería el problema que el lote viene a
        // resolver. El refresco lo hace el pull de más abajo, que en la misma
        // corrida se trae todo lo que cambió -- un alta o una edición dejan al
        // cliente con el contador más alto, así que entra seguro en ese filtro.
        //
        // syncClientById se queda igual: la usan el botón de cada fila y el alta
        // de cliente desde la factura, donde sí se necesita el registro completo
        // en el momento.
        const lote = await enviarLote(
          "Importaciones_insertaClientes",
          transformClientsForBC(aEnviar, await getActiveCountryCode()),
          aEnviar.length,
          "clientes"
        );

        // El cruce es por posición: BC devuelve los resultados en el mismo orden
        // en que recibió los clientes. Por No_ no se podría, porque todas las
        // altas se mandan como "TEMP" y volverían indistinguibles entre sí.
        for (let indice = 0; indice < aEnviar.length; indice++) {
          const client = aEnviar[indice];
          const resultado = lote.results[indice];
          let traiaBajaRechazada = false;

          if (client.deleted) {
            if (resultado.ok) {
              await db.clients.delete(client.id);
            } else {
              // BC no aplicó la baja: el cliente NO se borra en local, se
              // revierte el borrado y el motivo queda guardado en él.
              await restoreDeletedClient(client, resultado.message);
              failedClients.push({ client, action: "delete", reason: resultado.message });
            }
          } else if (resultado.ok && resultado.assignedNo) {
            // Se anota antes del update, que es lo que borra el motivo anterior.
            traiaBajaRechazada = Boolean(client.syncError);

            await db.clients.update(client.id, {
              No: resultado.assignedNo,
              lastModified: new Date(),
              lastSynced: new Date(),
              pendingSync: 0,
              syncError: null,
              // El aviso de dimensiones, igual que en `syncClientById`: se guarda
              // si viene y se limpia si no. El cuadro recibe el mensaje entero
              // más abajo. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`,
              // fila H5 · TPV.
              avisoDeBC: avisoDeDimensiones(resultado.message),
            });

            // Las facturas que apuntaban al cliente local todavía sin número
            // tienen que quedarse con el que acaba de asignar BC.
            await updateClientNoInInvoices(client.id, resultado.assignedNo);
          } else {
            const motivo = motivoDeGuardadoFallido(resultado);
            failedClients.push({ client, action: "save", reason: motivo });

            // El motivo se guarda en el propio cliente. Sin esto el registro se
            // queda como pendiente, con aspecto de no haberse intentado nunca, y
            // la siguiente sincronización lo reenvía igual: el mismo rechazo, en
            // bucle, y nadie sabe por qué. Con `syncError` puesto sale en rojo
            // con el motivo de Business Central y el reintento pasa a ser del
            // cajero, que es quien puede corregir el dato.
            await db.clients.update(client.id, {
              syncError: motivo,
              pendingSync: 0,
              lastModified: new Date(),
            });
          }

          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: nombreDeCliente(client),
                no: resultado.assignedNo || client.No || null,
                accion: resultado.action || (client.deleted ? "deleted" : "saved"),
                ok: client.deleted ? resultado.ok : resultado.ok && Boolean(resultado.assignedNo),
                mensaje: conAvisoDeBajaRechazada(resultado.message || "", traiaBajaRechazada),
              },
              client.id,
              client.deleted ? "baja" : undefined
            )
          );
        }
      }
    }

    const localClients = await db.clients.toArray();

    // Con colchón: se vuelve a pedir desde la marca número `FILAS_DE_SOLAPE`,
    // no desde la mayor. Ver `margenDeSolape.js` — un contador que retrocede
    // deja la ficha por debajo de la marca y no vuelve nunca.
    //
    // Y sin `sort` restando: esta tabla incluye las altas hechas en el
    // terminal, que aún no tienen contador de Business Central, y con un
    // `undefined` dentro el comparador devuelve `NaN` y el orden queda sin
    // definir --así que «el último» podía no ser el máximo--.
    const lastSourceCode = marcaConSolape(localClients);

    const countryCode = await getActiveCountryCode();

    const localClientsByNo = {};
    localClients.forEach((client) => {
      if (client.No) localClientsByNo[client.No] = client;
    });

    // Filas locales que ya se llevó un cliente de esta corrida. Sin esto, varios
    // clientes de BC que compartan documento se llevarían la misma, y el
    // `bulkPut` los aplicaría todos sobre el mismo `id`: gana el último y los
    // demás no se crean nunca.
    const reclamadas = new Set();

    // Devuelve el registro a guardar por cada cliente del servidor, o null si
    // no hay que tocarlo. Mismo criterio que antes: un cliente con cambios
    // locales sin sincronizar (pendingSync 1 y ya con No) no se pisa con BC.
    const buildClientRecord = (serverClient) => {
      let localClient = localClientsByNo[serverClient.No];
      // El respaldo por documento sólo vale si el documento identifica a
      // alguien. `"0"` es verdadero en JavaScript, así que la comprobación
      // anterior lo daba por bueno: cada «No contribuyente» de Costa Rica que
      // llegara de BC se emparejaba con **la primera fila local que tuviera
      // cero** y el `bulkPut` la sobrescribía. Un pull de diez no contribuyentes
      // nuevos machacaba diez veces la misma fila y no creaba ninguno.
      //
      // Mientras sólo podía existir un cliente con cero, esto no se veía. Al
      // permitir varios, pasa de teórico a corriente -- y el daño es el peor de
      // todos, porque borra datos en vez de rechazar un alta.
      //
      // El tipo entra por la misma razón que en el alta: una cédula física y un
      // relleno con los mismos dígitos no son la misma persona.
      //
      // Y sólo puede reclamar una fila **que nunca llegó a Business Central**.
      // Una fila que ya tiene `No` está identificada por ese `No`: si el que
      // llega es otro, es otro cliente, por mucho que compartan el documento. En
      // Costa Rica hay 250 documentos que más de un cliente comparte --cédulas
      // jurídicas sobre todo--, así que sin esta condición un cliente nuevo de
      // BC se llevaba por delante la ficha local de su gemelo: mismo `id` en el
      // `bulkPut` dos veces, y gana el último. Es el mismo daño del caso 76 por
      // otra puerta.
      if (!localClient && documentoIdentifica(serverClient.Numero_Identificacion_fiscal)) {
        localClient = localClients.find(
          (c) =>
            !c.No &&
            !reclamadas.has(c.id) &&
            c.Numero_Identificacion_fiscal === serverClient.Numero_Identificacion_fiscal &&
            !c.deleted &&
            mismoEspacioDeIdentidad(serverClient, c, countryCode)
        );
        // Dos clientes de BC con el mismo documento no pueden llevarse la misma
        // fila: el segundo se crea aparte en vez de pisar al primero.
        if (localClient) reclamadas.add(localClient.id);
      }

      const mapped = mapServerClientToLocal(serverClient, countryCode, localClient);

      if (localClient) {
        if (localClient.pendingSync !== 0 && localClient.No) return null;
        // bulkPut con el id existente actualiza en vez de duplicar, y el spread
        // conserva el resto del registro local igual que hacía db.clients.update.
        return { ...localClient, ...mapped, id: localClient.id };
      }

      return { ...mapped, pendingSync: 0, deleted: false };
    };

    // Se trae por bloques y se guarda cada uno apenas llega, en vez de una sola
    // respuesta de `9 MB seguida de 18.643 escrituras secuenciales a IndexedDB.
    //
    // Se pagina por Source_Counter, no por desplazamiento: cada bloque pide lo
    // posterior al último traído, así que insertar o borrar un cliente entre dos
    // bloques no corre el resto ni hace que se salte ninguno. Medido: 18.643
    // clientes con 18.643 valores distintos, así que la clave es única.
    const traidos = await fetchPagedFromBusinessCentral("ListaClientes", {
      filter: lastSourceCode ? `Source_Counter gt ${lastSourceCode}` : "",
      keyField: "Source_Counter",
      onPage: async (page, procesados) => {
        const records = page.map(buildClientRecord).filter(Boolean);
        if (records.length) await db.clients.bulkPut(records);
        anotarAvanceDeLectura(ENTIDAD, procesados);
      },
    });

    anotarLectura(ENTIDAD, { guardados: traidos, incremental: Boolean(lastSourceCode) });

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedClients.length > 0) {
      const deletes = failedClients.filter((f) => f.action === "delete");
      const saves = failedClients.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} cliente(s) no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(`${saves.length} cliente(s) no se pudieron guardar en Business Central`);
      }

      // Motivo concreto que devolvió BC, si lo hubo.
      const reason = failedClients.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedClients.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos, y `executeWithRetry` sólo mira
    // `success === false`, así que un objeto acá no cambia nada para syncAll.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};
